/**
 * Bot Config Form Component
 * Reusable form for bot configuration parameters (used in create and edit flows)
 */

import type { BotConfig } from '../../services/bots.service'

interface BotConfigFormProps {
  config: BotConfig
  onChange: (config: BotConfig) => void
  errors?: Record<string, string>
  showActions?: boolean
  onSubmit?: () => void
  onCancel?: () => void
  submitLabel?: string
  cancelLabel?: string
  isLoading?: boolean
}

export function BotConfigForm({
  config,
  onChange,
  errors = {},
  showActions = true,
  onSubmit,
  onCancel,
  submitLabel = '確認',
  cancelLabel = '取消',
  isLoading = false,
}: BotConfigFormProps) {
  return (
    <div style={styles.container}>
      <div style={styles.configGrid}>
        {/* Rate Settings */}
        <div style={styles.configSection}>
          <h4 style={styles.configSectionTitle}>💰 利率設定</h4>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              最低日利率 (%) <span style={styles.required}>*</span>
            </label>
            <input
              type="number"
              step="0.0001"
              value={config.min_daily_rate * 100}
              onChange={(e) => onChange({ ...config, min_daily_rate: parseFloat(e.target.value) / 100 })}
              style={{
                ...styles.input,
                ...(errors.min_daily_rate ? styles.inputError : {}),
              }}
            />
            {errors.min_daily_rate && <ErrorMessage message={errors.min_daily_rate} />}
            {!errors.min_daily_rate && (
              <span style={styles.helpText}>
                年化約 {(config.min_daily_rate * 365 * 100).toFixed(2)}% APR
              </span>
            )}
          </div>
        </div>

        {/* Gap Settings */}
        <div style={styles.configSection}>
          <h4 style={styles.configSectionTitle}>📊 Gap 設定</h4>

          <div style={styles.formGroup}>
            <label style={styles.label}>Gap Mode</label>
            <select
              value={config.gap_mode}
              onChange={(e) => onChange({ ...config, gap_mode: e.target.value as 'raw' | 'rawbtc' | 'relative' })}
              style={styles.select}
            >
              <option value="relative">Relative (百分比)</option>
              <option value="raw">Raw (原始金額)</option>
              <option value="rawbtc">Raw BTC (BTC 等值)</option>
            </select>
            <span style={styles.helpText}>
              {config.gap_mode === 'relative' && '基於 orderbook 總深度的百分比'}
              {config.gap_mode === 'raw' && '使用原始貨幣金額'}
              {config.gap_mode === 'rawbtc' && '使用 BTC 等值金額'}
            </span>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Gap Bottom <span style={styles.required}>*</span>
              </label>
              <input
                type="number"
                step="0.1"
                value={config.gap_bottom}
                onChange={(e) => onChange({ ...config, gap_bottom: parseFloat(e.target.value) })}
                style={{
                  ...styles.input,
                  ...(errors.gap_bottom ? styles.inputError : {}),
                }}
              />
              {errors.gap_bottom && <ErrorMessage message={errors.gap_bottom} />}
              {!errors.gap_bottom && (
                <span style={styles.helpText}>
                  {config.gap_mode === 'relative' ? `前 ${config.gap_bottom}%` : `${config.gap_bottom} 單位`}
                </span>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Gap Top <span style={styles.required}>*</span>
              </label>
              <input
                type="number"
                step="0.1"
                value={config.gap_top}
                onChange={(e) => onChange({ ...config, gap_top: parseFloat(e.target.value) })}
                style={{
                  ...styles.input,
                  ...(errors.gap_top ? styles.inputError : {}),
                }}
              />
              {errors.gap_top && <ErrorMessage message={errors.gap_top} />}
              {!errors.gap_top && (
                <span style={styles.helpText}>
                  {config.gap_mode === 'relative' ? `前 ${config.gap_top}%` : `${config.gap_top} 單位`}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Spread Settings */}
        <div style={styles.configSection}>
          <h4 style={styles.configSectionTitle}>📈 分散設定</h4>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Spread Lend (筆數) <span style={styles.required}>*</span>
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={config.spread_lend}
                onChange={(e) => onChange({ ...config, spread_lend: parseInt(e.target.value) })}
                style={{
                  ...styles.input,
                  ...(errors.spread_lend ? styles.inputError : {}),
                }}
              />
              {errors.spread_lend && <ErrorMessage message={errors.spread_lend} />}
              {!errors.spread_lend && (
                <span style={styles.helpText}>將資金分散成多少筆訂單 (1-100)</span>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                最小放貸金額 <span style={styles.required}>*</span>
              </label>
              <input
                type="number"
                min="50"
                value={config.min_loan_size}
                onChange={(e) => onChange({ ...config, min_loan_size: parseFloat(e.target.value) })}
                style={{
                  ...styles.input,
                  ...(errors.min_loan_size ? styles.inputError : {}),
                }}
              />
              {errors.min_loan_size && <ErrorMessage message={errors.min_loan_size} />}
              {!errors.min_loan_size && <span style={styles.helpText}>建議至少 50 單位</span>}
            </div>
          </div>
        </div>

        {/* Limits */}
        <div style={styles.configSection}>
          <h4 style={styles.configSectionTitle}>⚙️ 限制</h4>

          <div style={styles.formGroup}>
            <label style={styles.label}>最大放貸百分比 (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={config.max_percent_to_lend || 100}
              onChange={(e) => onChange({ ...config, max_percent_to_lend: parseFloat(e.target.value) })}
              style={styles.input}
            />
            <span style={styles.helpText}>最多使用多少比例的餘額進行放貸 (0-100%)</span>
          </div>

          {config.max_active_amount !== undefined && (
            <div style={styles.formGroup}>
              <label style={styles.label}>最大活躍放貸金額</label>
              <input
                type="number"
                min="0"
                value={config.max_active_amount || 0}
                onChange={(e) =>
                  onChange({
                    ...config,
                    max_active_amount: parseFloat(e.target.value) || undefined,
                  })
                }
                style={styles.input}
              />
              <span style={styles.helpText}>限制總放貸金額上限（包含掛單 + 已放貸）</span>
            </div>
          )}
        </div>

        {/* Extended Period (xday) Settings */}
        <div style={styles.configSection}>
          <h4 style={styles.configSectionTitle}>⏰ 長期鎖定設定</h4>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>觸發門檻 (日利率 %)</label>
              <input
                type="number"
                step="0.0001"
                value={(config.xday_threshold || 0) * 100}
                onChange={(e) =>
                  onChange({
                    ...config,
                    xday_threshold: parseFloat(e.target.value) / 100 || undefined,
                  })
                }
                style={styles.input}
              />
              <span style={styles.helpText}>高於此利率時使用長期鎖定</span>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>鎖定天數</label>
              <input
                type="number"
                min="2"
                max="120"
                value={config.xdays || 2}
                onChange={(e) =>
                  onChange({
                    ...config,
                    xdays: parseInt(e.target.value) || undefined,
                  })
                }
                style={styles.input}
              />
              <span style={styles.helpText}>達到門檻時鎖定的天數 (2-120)</span>
            </div>
          </div>
        </div>

        {/* Options */}
        <div style={styles.configSection}>
          <h4 style={styles.configSectionTitle}>🔧 其他選項</h4>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={config.auto_transfer}
                onChange={(e) => onChange({ ...config, auto_transfer: e.target.checked })}
                style={styles.checkbox}
              />
              <span>
                <strong>自動轉帳</strong>
                <span style={styles.checkboxSubtext}>
                  從 Exchange 錢包自動轉帳到 Funding 錢包
                </span>
              </span>
            </label>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={config.dry_run || false}
                onChange={(e) => onChange({ ...config, dry_run: e.target.checked })}
                style={styles.checkbox}
              />
              <span>
                <strong>測試模式</strong>
                <span style={styles.checkboxSubtext}>不實際提交訂單，僅模擬運行</span>
              </span>
            </label>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>更新間隔 (秒)</label>
            <input
              type="number"
              min="60"
              max="3600"
              value={config.update_interval}
              onChange={(e) => onChange({ ...config, update_interval: parseInt(e.target.value) })}
              style={{
                ...styles.input,
                ...(errors.update_interval ? styles.inputError : {}),
              }}
            />
            {errors.update_interval && <ErrorMessage message={errors.update_interval} />}
            {!errors.update_interval && (
              <span style={styles.helpText}>Bot 執行週期間隔 (60-3600 秒)</span>
            )}
          </div>
        </div>
      </div>

      {showActions && (
        <div style={styles.actions}>
          {onCancel && (
            <button onClick={onCancel} style={styles.cancelButton} disabled={isLoading}>
              {cancelLabel}
            </button>
          )}
          {onSubmit && (
            <button onClick={onSubmit} style={styles.submitButton} disabled={isLoading}>
              {isLoading ? '處理中...' : submitLabel}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Error Message Component
 */
function ErrorMessage({ message }: { message: string }) {
  return (
    <div style={styles.errorMessage}>
      <span style={styles.errorIcon}>⚠️</span>
      <span style={styles.errorText}>{message}</span>
    </div>
  )
}

// ============================================================================
// Styles
// ============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
  },
  configSection: {
    padding: '24px',
    backgroundColor: '#f9fafb',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
  },
  configSectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '8px',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  inputError: {
    borderColor: '#ef4444',
    backgroundColor: '#fef2f2',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
    backgroundColor: 'white',
  },
  helpText: {
    display: 'block',
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '6px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  checkboxGroup: {
    marginBottom: '20px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    marginTop: '2px',
    flexShrink: 0,
  },
  checkboxSubtext: {
    display: 'block',
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: 'normal',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  submitButton: {
    padding: '12px 32px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    marginTop: '8px',
  },
  errorIcon: {
    fontSize: '14px',
    flexShrink: 0,
  },
  errorText: {
    fontSize: '13px',
    color: '#dc2626',
    fontWeight: '500',
  },
}
